const API_BASE = 'https://statsapi.mlb.com/api/v1';
const MLB_HOSTS = ['mlb.com', 'www.mlb.com', 'mlbstatic.com', 'www.mlbstatic.com'];

const textValue = (...values) => (
  values.find((value) => typeof value === 'string' && value.trim()) || ''
);

export const safeMlbUrl = (value) => {
  if (!value) return null;

  try {
    const url = new URL(value, 'https://www.mlb.com');
    const allowed = MLB_HOSTS.some(
      (host) => url.hostname === host || url.hostname.endsWith(`.${host}`)
    );

    return url.protocol === 'https:' && allowed ? url.href : null;
  } catch {
    return null;
  }
};

const getImage = (item) => {
  const cuts = item?.image?.cuts || item?.photo?.cuts || [];
  const preferred = cuts
    .filter((cut) => cut?.src)
    .sort((a, b) => Number(b.width || 0) - Number(a.width || 0))[0];

  return preferred?.src || item?.image?.src || item?.photo?.src || null;
};

const getVideoPlaybackUrl = (item) => {
  const playbacks = item?.playbacks || [];
  const preferred = playbacks.find((entry) => {
    const name = String(entry?.name || '').toLowerCase();
    const url = String(entry?.url || '').toLowerCase();

    return entry?.url && (
      name.includes('mp4') ||
      name.includes('avc') ||
      url.includes('.mp4')
    );
  });

  return safeMlbUrl(preferred?.url);
};

const getExternalUrl = (item, type = 'news') => {
  const direct = safeMlbUrl(item?.url || item?.link);
  if (direct) return direct;

  const slug = item?.slug || item?.seoTitle;
  if (!slug) return null;

  const section = type === 'video' ? 'video' : 'news';
  return safeMlbUrl(`https://www.mlb.com/${section}/${slug}`);
};

const normalizeVideo = (item, game) => {
  const title = textValue(item?.title, item?.headline, 'MLB video highlight');
  const description = textValue(item?.description, item?.blurb);
  const playbackUrl = getVideoPlaybackUrl(item);
  const externalUrl = getExternalUrl(item, 'video') || playbackUrl;

  return {
    id: `video-${game.gamePk}-${item?.id || title}`,
    type: 'video',
    title,
    description,
    url: externalUrl,
    playbackUrl,
    image: getImage(item),
    duration: item?.duration || '',
    gamePk: game.gamePk,
    gameDate: game.officialDate,
    opponent: game.opponent,
    matchup: game.matchup,
    searchText: `${title} ${description} ${game.opponent} ${game.matchup}`.toLowerCase()
  };
};

const normalizeArticle = (item, game, section) => {
  if (!item) return null;

  const title = textValue(item?.headline, item?.title, `${game.matchup} ${section}`);
  const description = textValue(item?.blurb, item?.description, item?.subhead);

  return {
    id: `article-${game.gamePk}-${item?.id || section}`,
    type: 'news',
    title,
    description,
    url: getExternalUrl(item, 'news'),
    playbackUrl: null,
    image: getImage(item),
    duration: '',
    gamePk: game.gamePk,
    gameDate: game.officialDate,
    opponent: game.opponent,
    matchup: game.matchup,
    searchText: `${title} ${description} ${game.opponent} ${game.matchup}`.toLowerCase()
  };
};

const normalizeGameContent = (content, game) => {
  const highlights = content?.highlights?.highlights?.items || [];
  const editorial = content?.editorial || {};
  const articles = [
    ['Recap', editorial?.recap?.mlb],
    ['Preview', editorial?.preview?.mlb],
    ['Article', editorial?.articles?.items?.[0]]
  ];

  return [
    ...highlights.map((item) => normalizeVideo(item, game)),
    ...articles
      .map(([section, item]) => normalizeArticle(item, game, section))
      .filter(Boolean)
  ];
};

const makeGameContext = (game) => {
  const redSoxHome = game?.teams?.home?.team?.id === 111;
  const opponent = redSoxHome
    ? game?.teams?.away?.team
    : game?.teams?.home?.team;
  const away = game?.teams?.away?.team?.abbreviation || 'AWY';
  const home = game?.teams?.home?.team?.abbreviation || 'HME';

  return {
    gamePk: game.gamePk,
    officialDate: game.officialDate || game.gameDate?.slice(0, 10) || '',
    opponent: opponent?.name || opponent?.abbreviation || 'Opponent',
    matchup: `${away} at ${home}`
  };
};

export async function fetchMlbContentHub(teamId = 111, limit = 12) {
  const year = new Date().getFullYear();
  const scheduleUrl = `${API_BASE}/schedule?sportId=1&teamId=${teamId}&season=${year}&hydrate=team`;

  const [scheduleRes, rosterRes] = await Promise.all([
    fetch(scheduleUrl),
    fetch(`${API_BASE}/teams/${teamId}/roster?rosterType=active`)
  ]);

  if (!scheduleRes.ok) {
    throw new Error('MLB schedule content is unavailable.');
  }

  const schedule = await scheduleRes.json();
  const roster = rosterRes.ok ? await rosterRes.json() : { roster: [] };
  const games = (schedule?.dates || [])
    .flatMap((date) => date.games || [])
    .filter((game) => game.status?.abstractGameState === 'Final')
    .sort((a, b) => new Date(b.gameDate) - new Date(a.gameDate))
    .slice(0, limit)
    .map(makeGameContext);

  const responses = await Promise.all(
    games.map(async (game) => {
      try {
        const res = await fetch(`${API_BASE}/game/${game.gamePk}/content`);
        if (!res.ok) return [];
        return normalizeGameContent(await res.json(), game);
      } catch {
        return [];
      }
    })
  );

  const unique = new Map();
  responses.flat().forEach((item) => unique.set(item.id, item));

  return {
    items: [...unique.values()],
    games,
    players: (roster?.roster || [])
      .map((entry) => ({
        id: entry.person?.id,
        name: entry.person?.fullName || '',
        position: entry.position?.abbreviation || ''
      }))
      .filter((player) => player.id && player.name)
  };
}

export const createMlbSearchUrl = (query) => {
  const value = String(query || 'Boston Red Sox').trim();
  return `https://www.mlb.com/search?query=${encodeURIComponent(value)}`;
};